/**
 * Privacy — user-facing privacy statement.
 *
 * Written in warm, plain English (aligned with Rammy's "honest by design"
 * voice). This is a STARTER document — before public launch, run it past
 * counsel and swap the placeholder contact + jurisdiction lines.
 */
import LegalLayout from "@/pages/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy" updated="February 2026" testid="page-privacy">
      <p>
        Rammy is a personal AI companion. That means we sit close to your day —
        your calendar, your notes, your health data if you connect it. This
        page tells you, plainly, what we collect, why, where it lives, and how
        you stay in control.
      </p>

      <h2>1. What we collect</h2>
      <ul>
        <li><strong>Account data</strong> — email, name, password hash, and (if you sign in with Google) your Google account ID.</li>
        <li><strong>Conversations</strong> — the text and voice you exchange with Rammy, plus any images you upload.</li>
        <li><strong>Memory Garden</strong> — the meaningful facts Rammy extracts from your chats (goals, preferences, people you mention). You can view, edit, or delete every entry.</li>
        <li><strong>Connected accounts</strong> — read-only access tokens for services you explicitly connect (Google Calendar, Gmail, Telegram, etc.).</li>
        <li><strong>Usage telemetry</strong> — anonymised counts of feature use (how many chats, how many voice minutes) to keep the service healthy. Never sold.</li>
      </ul>

      <h2>2. What we do NOT collect</h2>
      <ul>
        <li>We do not read your Gmail, Drive, or Calendar without your explicit consent per integration.</li>
        <li>We do not sell your data to advertisers. There are no ads on Rammy.</li>
        <li>We do not use your conversations to train third-party foundation models.</li>
        <li>We do not fingerprint you across other websites or apps.</li>
      </ul>

      <h2>3. How Rammy uses AI</h2>
      <p>
        Rammy sends parts of your conversation to third-party AI providers
        (OpenAI, Anthropic, Google) via the Emergent LLM proxy so the assistant
        can reply. Only the message content needed for that reply is sent — not
        your entire history, not your account details.
      </p>
      <p>
        Your Memory Garden entries stay on <strong>our</strong> database. They
        are never shared with any external LLM provider except as short
        contextual snippets attached to the current turn (so Rammy can answer
        "when's my sister's birthday?" using memories you saved).
      </p>

      <h2>4. Voice data</h2>
      <p>
        When you use voice chat, your microphone audio streams to Deepgram
        (speech-to-text) and, for the reply, Rammy streams generated audio back
        via ElevenLabs. Neither provider retains your audio for training under
        our contracted terms. We keep the <strong>transcript</strong> of what
        you said as part of the conversation history — the raw audio is
        discarded once the transcript is produced.
      </p>
      <p>
        Voice cloning ("My Voice") is opt-in only, requires an explicit
        two-checkbox consent, and is built from a 60-180 second guided
        enrollment. Live conversation audio is <strong>never</strong> used to
        clone your voice.
      </p>

      <h2>5. Where your data lives</h2>
      <p>
        Rammy's servers run in secure cloud infrastructure in the United States.
        MongoDB stores account, conversation, and Memory Garden data. All
        connections between your browser and Rammy, and between Rammy and third
        parties, use TLS.
      </p>

      <h2>6. Your rights + how to exercise them</h2>
      <ul>
        <li><strong>See it</strong> — every memory, integration grant, and past conversation is visible inside the app.</li>
        <li><strong>Edit it</strong> — memories are editable; connected accounts can be individually revoked.</li>
        <li><strong>Delete it</strong> — you can delete any single memory, any single conversation, or your entire account at any time from Settings. Account deletion cascades to conversations, memories, connected-account tokens, and voice clones.</li>
        <li><strong>Export it</strong> — email <a href="mailto:privacy@rammy.ai">privacy@rammy.ai</a> and we'll send you a JSON dump of everything associated with your account within 30 days.</li>
      </ul>

      <h2>7. Third parties we use</h2>
      <ul>
        <li>OpenAI, Anthropic, Google (LLMs — via the Emergent LLM proxy)</li>
        <li>Deepgram (speech-to-text) · ElevenLabs (text-to-speech, opt-in voice cloning)</li>
        <li>Stripe (payment processing, if you subscribe to Plus or Pro)</li>
        <li>Google, Apple (authentication providers, if you choose social sign-in)</li>
        <li>Various data adapters (TMDB, YouTube, Wikidata, SEC EDGAR, Currents, Semantic Scholar, Crossref) — invoked with anonymous requests, no user identifier attached</li>
      </ul>

      <h2>8. Cookies + local storage</h2>
      <p>
        Rammy uses HTTP-only session cookies for authentication and a small
        number of localStorage entries for user preferences (voice choice,
        show-sources toggle, exact-location consent). We do not use tracking
        pixels, third-party ad cookies, or session replay tools.
      </p>

      <h2>9. Children</h2>
      <p>
        Rammy is not directed at children under 13 and we do not knowingly
        collect data from them. If you believe a child has created an account,
        please email us and we will delete it.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        When we make material changes we'll email registered users, update the
        "Last updated" date above, and post a note on the changelog. Continued
        use of Rammy after a change means you accept the updated policy.
      </p>

      <h2>11. Contact</h2>
      <p>
        Privacy questions or requests → <a href="mailto:privacy@rammy.ai">privacy@rammy.ai</a>.
        General questions → <a href="mailto:hello@rammy.ai">hello@rammy.ai</a>.
      </p>
    </LegalLayout>
  );
}
